import { toast } from 'react-hot-toast';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { request } from '@core/api/request';

// Channels (WhatsApp / Facebook / Instagram / the public-widget key) sit
// flat under /crm/, unlike conversations which are nested under
// /crm/inbox/. `apiClient`'s baseURL already ends in `/api/v1/crm/` — do
// NOT prefix these with `crm/`, and do NOT nest them under `inbox/`
// (`inbox/channels/` 404s). See `src/features/conversations/services/
// conversations-api.ts` for the sibling endpoint that got this wrong once.
export type ChannelKind =
  | 'FACEBOOK'
  | 'INSTAGRAM'
  | 'WHATSAPP'
  | 'SNAPCHAT'
  | 'TIKTOK';

export interface ChannelAccount {
  id: number;
  channel: ChannelKind | 'WIDGET';
  external_id: string;
  site_key: string;
  is_enabled: boolean;
  send_via: string;
  send_id: string;
  created_at: string;
  // `secret` is write-only on the backend — accepted on POST/PATCH, never
  // returned. Deliberately absent from this read type.
  /**
   * The Page/account name Meta returned during OAuth discovery (e.g. "Acme
   * Support") — empty string when unknown, never null. Prefer this over
   * `external_id` wherever a human reads the tile; a numeric id doesn't
   * tell an operator which Page or account is actually connected.
   */
  display_name?: string;
  /**
   * The human-readable phone number — WhatsApp rows only, where
   * `display_name` carries the verified business name instead.
   */
  phone_number?: string;
  /**
   * When the current token dies, ISO datetime or null. A row with
   * `is_enabled: false` and an `expires_at` in the past means the token
   * expired on its own — distinct from an operator disabling it on
   * purpose, which is what `channel-tile.tsx`'s "Needs reconnection" state
   * is for.
   */
  expires_at?: string | null;
  /** When the token behind this row was last refreshed, ISO datetime or null. */
  refreshed_at?: string | null;
  /**
   * The backend flagged this row's token as dead after consecutive send
   * failures — the channel receives webhooks but cannot reply, and the
   * agent skips it. Fixed by re-running OAuth, same as an expired token.
   */
  needs_reauth?: boolean;
  /** Whether the assistant answers this channel at all. Channels start OFF;
   * per-conversation flags are overridden, never rewritten, while this is
   * off. WIDGET rows are always true — the widget's only responder IS the
   * assistant, so the backend refuses turning it off. */
  ai_enabled: boolean;
  /** The persona answering conversations that have no agent of their own.
   * Required (server-enforced) whenever a Meta channel has AI enabled. */
  default_ai_agent: number | null;
  /** The default persona's name, for the tile/drawer — saves a lookup. */
  default_ai_agent_name: string | null;
}

export interface ConnectChannelInput {
  channel: ChannelKind;
  external_id: string;
  secret: string;
  send_via: string;
  send_id?: string;
  /**
   * WhatsApp only: the WhatsApp Business Account id. When provided, the
   * backend subscribes the WABA for webhooks as part of the connect and
   * refuses the create if the subscription does not register — closing the
   * connected-but-receives-nothing gap the old management-command flow left.
   */
  waba_id?: string;
}

export type UpdateChannelInput = { id: number } & Partial<
  Omit<ConnectChannelInput, 'channel'>
> & {
    is_enabled?: boolean;
    /** Owner-editable label; seeded from the Page/account name at OAuth. */
    display_name?: string;
    ai_enabled?: boolean;
    default_ai_agent?: number | null;
  };

interface PaginatedEnvelope<T> {
  count: number;
  page_size: number;
  page_number: number;
  total_pages: number;
  results: T[];
}

const CHANNELS_QUERY_KEY = ['channels'];

/** The channels endpoint's own page size, matching the backend paginator's
 * `max_page_size`. Requested explicitly — the paginator's *default* page
 * size is 10, which would otherwise silently truncate an organization with
 * more than 10 rows (WIDGET rows count toward the same page). The list is
 * ordered `-id`, so what falls off page 1 is the *oldest* rows — i.e. the
 * first channels ever connected — rendering them as "Not connected" with
 * no error. */
const CHANNELS_PAGE_SIZE = 100;

/** GET channels/ — unwraps the paginated envelope into the plain list. */
export const listChannels = async (): Promise<ChannelAccount[]> => {
  const response = await request<PaginatedEnvelope<ChannelAccount>>({
    url: 'channels/',
    method: 'GET',
    params: { page_size: CHANNELS_PAGE_SIZE },
  });

  return response.results;
};

export const useChannels = () => {
  return useQuery({
    queryKey: CHANNELS_QUERY_KEY,
    queryFn: listChannels,
  });
};

/** POST channels/ — connects a new channel. Writes are owner-only; the
 * backend returns 403 for non-owners, surfaced via onError below. */
export const connectChannel = async (
  data: ConnectChannelInput
): Promise<ChannelAccount> => {
  return request<ChannelAccount>({
    url: 'channels/',
    method: 'POST',
    body: data,
  });
};

export const useConnectChannel = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: connectChannel,
    onSuccess: () => {
      toast.success('Channel connected.');
    },
    onError: (error: string) => {
      toast.error(error);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: CHANNELS_QUERY_KEY });
    },
  });
};

/** PATCH channels/{id}/ — updates an existing channel (e.g. toggling
 * `is_enabled` or rotating `secret`). Owner-only, same as connect/disconnect. */
export const updateChannel = async ({
  id,
  ...data
}: UpdateChannelInput): Promise<ChannelAccount> => {
  return request<ChannelAccount>({
    url: `channels/${id}/`,
    method: 'PATCH',
    body: data,
  });
};

export const useUpdateChannel = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateChannel,
    onSuccess: () => {
      toast.success('Channel updated.');
    },
    onError: (error: string) => {
      toast.error(error);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: CHANNELS_QUERY_KEY });
      // Inbox agent toggle / channel_ai_enabled read conversation queries —
      // keep them in sync with Integration and Channels hub AI settings.
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
    },
  });
};

export interface ChannelAiToggle {
  id: number;
  ai_enabled: boolean;
}

/** PUT channels/ai-settings/ — the "AI Reply To" modal's Confirm. All-or-nothing
 * on the server: a 400 names the channels that cannot change and writes nothing.
 *
 * `preserveError: true` because the 400 is structured — `{accounts: {id: message}}`
 * — and the default rejection flattens it to one sentence, losing which channel it's about.
 */
export async function updateChannelAiSettings(
  accounts: ChannelAiToggle[]
): Promise<ChannelAccount[]> {
  return request<ChannelAccount[]>({
    url: 'channels/ai-settings/',
    method: 'PUT',
    body: { accounts },
    preserveError: true,
  });
}

/** DELETE channels/{id}/ — disconnects a channel. The row (and its
 * conversations) survive on the backend so history keeps its business
 * identity; reconnecting the same Page revives them. */
export const disconnectChannel = async (id: number): Promise<void> => {
  await request<void>({
    url: `channels/${id}/`,
    method: 'DELETE',
  });
};

export const useDisconnectChannel = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: disconnectChannel,
    onSuccess: () => {
      toast.success('Channel disconnected.');
    },
    onError: (error: string) => {
      toast.error(error);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: CHANNELS_QUERY_KEY });
    },
  });
};

/** DELETE channels/{id}/purge/ — deletes the channel outright, together
 * with every conversation and message it carried. Irreversible; the
 * confirm dialog's copy must say so. */
export const deleteChannel = async (id: number): Promise<void> => {
  await request<void>({
    url: `channels/${id}/purge/`,
    method: 'DELETE',
  });
};

export const useDeleteChannel = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteChannel,
    onSuccess: () => {
      toast.success('Channel deleted.');
    },
    onError: (error: string) => {
      toast.error(error);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: CHANNELS_QUERY_KEY });
      // Deleting a channel deletes its conversations too — any cached
      // inbox list is now stale.
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
    },
  });
};
